import { memo, type KeyboardEvent, type ReactNode } from 'react';
import { getDisplayEmoji } from '@/features/events/lib/eventEmoji.lib';
import { Confetti } from '@/features/experiences/components/Confetti';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext/useEffectiveExperience';
import {
  useEventExportStatus,
  useEventExportStatusInputs,
} from '@/features/interactions/hooks/useEventExportStatus';
import {
  useIsEventSelected,
  useSelectModeStore,
} from '@/features/interactions/multiExport/hooks/useSelectModeStore';
import type { PlatformTypes } from '@/types/gql.generated';
import { Badge, Box, Flex, Icon, Text } from '@/ui';
import { formatDateRange } from '@/utils/formatDate';
import { isCoveredBySubscription } from './rowSyncStatus.lib';
import type { ExportableEvent } from './useExportableEvents';

// How a row behaves for the chosen destination:
//  - select   — bulk-export platforms: the row toggles selection.
//  - weblink  — per-event platforms: the row adds this one event.
//  - email    — the row toggles this event's invite compose, which expands
//               inside the card.
//  - readonly — no per-row action (e.g. the ICS download snapshots everything).
export type RowMode = 'select' | 'weblink' | 'email' | 'readonly';

type Props = {
  row: ExportableEvent;
  mode: RowMode;
  /** The chosen destination, or null before one is picked. Drives the badge. */
  platform: PlatformTypes | null;
  // weblink: open this event's platform link. email: toggle this event's
  // compose form. The whole row is the control.
  onAdd?: () => void;
  addLabel?: string;
  /** readonly only: no destination chosen yet, so the row asks for one. */
  onRequestDestination?: () => void;
  /** email only: this row's compose is open — selected styling plus children. */
  isExpanded?: boolean;
  /** email only: this row just sent an invite — burst confetti from the card. */
  showConfetti?: boolean;
  /** email only: the expanded compose, rendered inside the card. */
  children?: ReactNode;
};

const formatWhen = ({ firstInRange, event }: ExportableEvent): string => {
  const dates = formatDateRange(firstInRange);
  if (event.isAllDay) return `${dates} · All day`;
  return `${dates} · ${firstInRange.startDate.toFormat('h:mm a')}`;
};

// Memoized so a selection toggle (which re-renders the whole body) only
// re-renders the one row whose own `useIsEventSelected` flipped, not the entire
// list. In `select` mode every prop is stable (row is memoized upstream; mode
// and platform are constant; the action props and children are undefined), so
// the memo is effective there.
export const ExportEventRow = memo(function ExportEventRow({
  row,
  mode,
  platform,
  onAdd,
  addLabel,
  onRequestDestination,
  isExpanded = false,
  showConfetti = false,
  children,
}: Props) {
  const { event, isRecurring } = row;
  const isSelected = useIsEventSelected(event.id);
  const toggleEvent = useSelectModeStore((state) => state.toggleEvent);
  // The rule the event cards use (SendEventButtons drives its success state
  // from this same hook), so a row and a card never disagree about an event.
  const { isSuccess: isAdded } = useEventExportStatus({ event, platform });
  const statusInputs = useEventExportStatusInputs();
  const { viewerCalendarSubscriptions } = useEffectiveExperience();
  const isSyncing = isCoveredBySubscription({
    event,
    platform,
    subscriptions: viewerCalendarSubscriptions,
    inputs: statusInputs,
  });
  // Matches the card the row stands for: a fed-in event with no emoji of its
  // own shows its source's default (getDisplayEmoji).
  const { experience } = usePageExperienceContext();
  const emoji = getDisplayEmoji(experience, event);

  const isSelect = mode === 'select';
  const isWeblink = mode === 'weblink';
  const isEmail = mode === 'email';
  const isRowAction = isWeblink || isEmail;
  const asksForDestination = mode === 'readonly' && !!onRequestDestination;
  const isInteractive = isSelect || isRowAction || asksForDestination;
  const active = (isSelect && isSelected) || (isEmail && isExpanded);

  const rowActionLabel = addLabel ?? 'Add to calendar';

  const handleActivate = () => {
    if (isSelect) {
      toggleEvent(event.id);
    } else if (isRowAction) {
      onAdd?.();
    } else if (asksForDestination) {
      onRequestDestination?.();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isInteractive) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate();
    }
  };

  return (
    <Box
      bg={active ? 'brand.40' : 'surface.card'}
      border="1px solid"
      borderColor={active ? 'brand.200' : 'line.card'}
      borderRadius="ah-lg"
      pos="relative"
      transition="border-color .15s, background .15s"
      w="100%"
      _hover={
        isInteractive
          ? { borderColor: active ? 'brand.300' : 'ink.soft' }
          : undefined
      }
    >
      {/* Card-anchored burst, mounted only on a fresh send. */}
      {showConfetti && <Confetti left="50%" pos="absolute" top="50%" />}
      {/* The whole row line is the control; the expanded compose below it is
          not, so clicks in the form don't collapse it. */}
      <Flex
        align="center"
        aria-checked={isSelect ? isSelected : undefined}
        aria-expanded={isEmail ? isExpanded : undefined}
        cursor={isInteractive ? 'pointer' : 'default'}
        data-group
        gap={2.5}
        px={3}
        py={2.5}
        tabIndex={isInteractive ? 0 : undefined}
        aria-label={
          isRowAction
            ? `${rowActionLabel}: ${event.title}`
            : asksForDestination
              ? `Choose a calendar to send ${event.title}`
              : undefined
        }
        role={
          isSelect
            ? 'checkbox'
            : isRowAction || asksForDestination
              ? 'button'
              : undefined
        }
        onClick={handleActivate}
        onKeyDown={handleKeyDown}
      >
        {isSelect && (
          <Flex
            align="center"
            aria-hidden
            bg={isSelected ? 'brand.500' : 'white'}
            border="2px solid"
            borderColor={isSelected ? 'brand.500' : 'line.DEFAULT'}
            borderRadius="5px"
            flexShrink={0}
            h="20px"
            justify="center"
            w="20px"
          >
            {isSelected && (
              <Icon color="white" icon="Checkmark" size={3} strokeWidth={3} />
            )}
          </Flex>
        )}
        {emoji && (
          <Text fontSize="md" lineHeight={1}>
            {emoji}
          </Text>
        )}
        <Box flex="1" minW={0}>
          <Flex align="center" gap={1.5} minW={0}>
            <Text
              color="ink.strong"
              fontSize="ah-row"
              fontWeight="semibold"
              isTruncated
            >
              {event.title}
            </Text>
            {isRecurring && (
              <Badge
                alignItems="center"
                bg="magicPurple.50"
                borderRadius="ah-md"
                color="magicPurple.600"
                display="inline-flex"
                flexShrink={0}
                gap={0.5}
                px={1.5}
              >
                <Icon icon="Repeat" size={3} />
                Repeats
              </Badge>
            )}
          </Flex>
          <Text color="ink.muted" fontSize="ah-status" isTruncated>
            {formatWhen(row)}
          </Text>
        </Box>
        {/* In flow (inheriting the row's centered alignment) rather than
            absolutely positioned, so the truncating title yields space to it
            instead of running under. Ordered ahead of the weblink arrow, which
            stays the row's rightmost element and so keeps its column aligned
            whether or not a row carries the badge. */}
        {isAdded && (
          <Badge
            bg="magicPurple.successSubtle"
            borderRadius="ah-pill"
            color="ink.soft"
            flexShrink={0}
            fontSize="ah-label"
            fontWeight={700}
            px={2}
            py="2px"
            textTransform="none"
          >
            {isSyncing ? 'Syncing' : isEmail ? '🎉 Sent' : '🎉 Added'}
          </Badge>
        )}
        {/* Decorative new-tab hint — the row itself is the button. */}
        {isWeblink && (
          <Icon
            _groupFocusWithin={{ opacity: 1, transform: 'translateX(0)' }}
            _groupHover={{ opacity: 1, transform: 'translateX(0)' }}
            aria-hidden
            color="ink.soft"
            flexShrink={0}
            icon="ArrowUpRight"
            opacity={0}
            size={4}
            transform="translateX(-4px)"
            transition="opacity .15s, transform .15s"
          />
        )}
      </Flex>
      {isExpanded && children && (
        <Box pb={3} px={3}>
          {children}
        </Box>
      )}
    </Box>
  );
});
